import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { compose, bindActionCreators } from 'redux';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Grid, useMediaQuery } from '@mui/material';
import Mail from '@mui/icons-material/Mail';
import { Card, Autocomplete } from '@totalsoft/rocket-ui';
import { loadGDPR, loadPartnerTypes, loadRelations } from 'features/offer/edit/client/form/actionCreators';
import { loadOfferConfiguration } from 'actions/actionCreators/offer/offerActions';
import SimulatorPFClientComponent from './SimulatorPFClientComponent';
import SimulatorPJClientComponent from './SimulatorPJClientComponent';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { get, set } from '@totalsoft/change-tracking-react';
import documentTypeIds from 'constants/documentTypeIds';
import { CompactFormWrapper } from 'features/simulator/SimulatorStyles';
import { ClientContainerWrappper } from '../../SimulatorStyles';

const SimulatorClientContainer = ({
  t,
  partnerLens,
  partnerTypes,
  actions,
  relations,
  gdpr,
  validation,
  editPartnerDisabled,
  onPartnerTypeChange
}) => {
  const matches = useMediaQuery('(min-width:1280px)');
  const localPartner = useMemo(() => partnerLens |> get, [partnerLens]);

  useEffect(() => {
    const partnerTypeId = partnerLens.partnerType.partnerTypeId |> get;
    const partnerType = partnerTypes && partnerTypes.find(x => x.partnerTypeId === partnerTypeId);
    if (partnerType) (partnerLens.partnerType |> set)(partnerType);
  }, [partnerTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    actions.loadOfferConfiguration(documentTypeIds.Offer);
    actions.loadPartnerTypes();
    actions.loadRelations();
  }, [actions]);

  return (
    <ClientContainerWrappper item xs={12} lg={8} matches={matches}>
      <Grid>
        <Card icon={Mail} title={t('OfferEdit.Client.ClientData')}>
          <CompactFormWrapper container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                valueKey='partnerTypeId'
                labelKey='partnerTypeName'
                label={t('Simulator.Client.PartnerType')}
                options={partnerTypes}
                value={localPartner.partnerType}
                onChange={onPartnerTypeChange}
                isClearable={false}
                disabled={editPartnerDisabled}
                error={!isValid(validation.partnerType.partnerTypeId)}
                helperText={getErrors(validation.partnerType.partnerTypeId)}
              />
            </Grid>
            {localPartner.partnerType && localPartner.partnerType?.typeCheck?.isPF ? (
              <SimulatorPFClientComponent
                relations={relations}
                partnerLens={partnerLens}
                loadGDPR={actions.loadGDPR}
                gdpr={gdpr}
                validation={validation}
                editPartnerDisabled={editPartnerDisabled}
              />
            ) : (
              <SimulatorPJClientComponent partnerLens={partnerLens} validation={validation} editPartnerDisabled={editPartnerDisabled} />
            )}
          </CompactFormWrapper>
        </Card>
      </Grid>
    </ClientContainerWrappper>
  );
};

SimulatorClientContainer.propTypes = {
  t: PropTypes.func.isRequired,
  partnerLens: PropTypes.object.isRequired,
  partnerTypes: PropTypes.array,
  actions: PropTypes.object.isRequired,
  relations: PropTypes.array,
  gdpr: PropTypes.object,
  validation: PropTypes.object.isRequired,
  editPartnerDisabled: PropTypes.bool.isRequired,
  onPartnerTypeChange: PropTypes.func.isRequired
};

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadPartnerTypes,
        loadRelations,
        loadGDPR,
        loadOfferConfiguration
      },
      dispatch
    )
  };
}

function mapStateToProps(state) {
  const partnerTypes = state.clientData.partnerTypes;
  const relations = state.clientData.relations;
  const gdpr = state.clientData.gdpr;
  const globalRights = state.globalRights;

  return {
    partnerTypes,
    relations,
    gdpr,
    globalRights
  };
}
export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(SimulatorClientContainer);
